import { useEffect, useRef, useState } from "react";
import { api, useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";

const blobCache = new Map();

export default function WorkerAvatar({ worker, size = "h-9 w-9", textSize = "text-xs", onPhotoChanged }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" && !!worker?.id;
  const [photoPath, setPhotoPath] = useState(worker?.photo_path || "");
  const [src, setSrc] = useState(() => (photoPath ? blobCache.get(photoPath) || null : null));
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setPhotoPath(worker?.photo_path || ""); }, [worker?.photo_path]);

  useEffect(() => {
    let alive = true;
    if (!photoPath) { setSrc(null); return; }
    if (blobCache.has(photoPath)) { setSrc(blobCache.get(photoPath)); return; }
    api.get(`/workers/${worker.id}/photo`, { responseType: "blob" })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        blobCache.set(photoPath, url);
        if (alive) setSrc(url);
      })
      .catch(() => { if (alive) setSrc(null); });
    return () => { alive = false; };
  }, [worker?.id, photoPath]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("File harus berupa gambar"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/workers/${worker.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      blobCache.set(res.data.photo_path, URL.createObjectURL(file));
      setPhotoPath(res.data.photo_path);
      toast.success("Foto pekerja diperbarui");
      onPhotoChanged?.(res.data.photo_path);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah foto");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const initials = (worker?.nama || "?")
    .split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";

  if (src) {
    return (
      <>
        <img src={src} alt={worker?.nama || "Foto"} data-testid={`worker-avatar-${worker?.id}`}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }} title="Klik untuk perbesar foto"
          className={`${size} rounded-full object-cover border border-slate-200 shrink-0 cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition-shadow`} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md p-3" onClick={(e) => e.stopPropagation()} data-testid={`worker-avatar-preview-${worker?.id}`}>
            <DialogTitle className="text-sm font-semibold text-slate-700">{worker?.nama || "Foto Pekerja"}</DialogTitle>
            <img src={src} alt={worker?.nama || "Foto"} className="w-full max-h-[70vh] rounded-lg object-contain bg-slate-100" />
            <div className="flex items-center justify-between gap-2">
              {worker?.nomor_pegawai && <p className="text-xs text-slate-500">{worker.nomor_pegawai}{worker?.jabatan ? ` · ${worker.jabatan}` : ""}</p>}
              {canEdit && (
                <>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid={`worker-avatar-file-${worker?.id}`}
                    onChange={(e) => upload(e.target.files?.[0])} />
                  <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`worker-avatar-ganti-${worker?.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60 transition-colors">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Ganti Foto
                  </button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return (
    <div data-testid={`worker-avatar-${worker?.id}`}
      className={`${size} rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold ${textSize} shrink-0`}>
      {initials}
    </div>
  );
}
